import { useEffect, useRef, useState } from 'react';
import { Ruler, Crosshair, Info, Check } from 'lucide-react';
import { HudCard } from '../hud';
import { toast } from 'sonner';

/**
 * PlanCalibrator
 * ---------------
 * Reusable 2-click + distance calibration overlay over any plan image.
 *
 *   1. User clicks "Calibrer" → enters `pick1` mode → clicks point A on plan
 *   2. Mode auto-advances to `pick2` → user clicks point B
 *   3. Input field appears → user enters real-world distance in meters
 *   4. Click "Calculer" → emits `onCalibrated({ scale_m_per_px })` to parent
 *
 * The component manages its own state and renders an SVG overlay above the
 * plan image (passed via `planUrl`). Parent controls everything else.
 *
 * Props
 *  - planUrl      : string (required) — image src for the plan
 *  - testidPrefix : string (optional) — prefix for data-testids (default "calib")
 *  - onCalibrated : ({scale_m_per_px}) => void — fired when scale is computed
 *  - onReset      : () => void (optional) — fired when user clears the scale
 *  - compact      : bool (optional) — collapse layout to fit a sidebar widget
 *  - aspectRatio  : string (optional) — viewport aspect-ratio (default '4 / 3')
 *  - children     : optional extra SVG nodes to render on top of the plan (eg.
 *                   a FOV cone). Useful when the calibrator is *part of* a
 *                   bigger interactive viewport (FpsCameraPlanner usage).
 */
const PlanCalibrator = ({
  planUrl,
  testidPrefix = 'calib',
  onCalibrated,
  onReset,
  compact = false,
  aspectRatio = '4 / 3',
}) => {
  const [mode, setMode] = useState('idle'); // idle | pick1 | pick2 | done | locked
  const [pointA, setPointA] = useState(null);  // {x_rel, y_rel}
  const [pointB, setPointB] = useState(null);
  const [meters, setMeters] = useState('');
  const [scaleMPerPx, setScaleMPerPx] = useState(null);
  const [planDims, setPlanDims] = useState({ w: 0, h: 0 });
  const viewportRef = useRef(null);
  const planImgRef = useRef(null);

  const handlePlanLoaded = (e) => {
    const img = e.currentTarget;
    setPlanDims({ w: img.clientWidth, h: img.clientHeight });
  };

  useEffect(() => {
    const onResize = () => {
      if (planImgRef.current) {
        setPlanDims({ w: planImgRef.current.clientWidth, h: planImgRef.current.clientHeight });
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const relFromEvent = (e) => {
    const vp = viewportRef.current;
    if (!vp) return null;
    const r = vp.getBoundingClientRect();
    return {
      x_rel: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)),
      y_rel: Math.max(0, Math.min(1, (e.clientY - r.top) / r.height)),
    };
  };

  const onViewportClick = (e) => {
    if (mode !== 'pick1' && mode !== 'pick2') return;
    const rel = relFromEvent(e);
    if (!rel) return;
    if (mode === 'pick1') {
      setPointA(rel);
      setMode('pick2');
      toast('Point A enregistré. Clique le point B (distance connue).', { icon: <Crosshair className="w-4 h-4" /> });
    } else {
      setPointB(rel);
      setMode('done');
      toast('Point B enregistré. Entre la distance réelle en mètres.', { icon: <Ruler className="w-4 h-4" /> });
    }
  };

  const compute = () => {
    const m = parseFloat(meters);
    if (!pointA || !pointB || !m || m <= 0) {
      toast.error('Renseigne 2 points et une distance > 0');
      return;
    }
    const dxRel = pointB.x_rel - pointA.x_rel;
    const dyRel = pointB.y_rel - pointA.y_rel;
    const pxDist = Math.sqrt((dxRel * planDims.w) ** 2 + (dyRel * planDims.h) ** 2);
    if (pxDist < 4) {
      toast.error('Points trop rapprochés — augmente la distance.');
      return;
    }
    const scale = m / pxDist;
    setScaleMPerPx(scale);
    setMode('locked');
    onCalibrated?.({ scale_m_per_px: scale });
    toast.success(`Calibration OK : 1 px = ${scale.toFixed(4)} m`);
  };

  const reset = () => {
    setMode('idle');
    setPointA(null);
    setPointB(null);
    setMeters('');
    setScaleMPerPx(null);
    onReset?.();
  };

  // Viewport size CSS — uses aspect-ratio so it stays responsive
  const viewportStyle = {
    cursor: mode === 'pick1' || mode === 'pick2' ? 'crosshair' : 'default',
    background: 'rgba(255,255,255,0.02)',
    minHeight: compact ? 240 : 320,
    maxHeight: compact ? 420 : 720,
    aspectRatio: planDims.w && planDims.h ? `${planDims.w} / ${planDims.h}` : aspectRatio,
  };

  return (
    <div className={compact ? 'space-y-3' : 'grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4'} data-testid={`${testidPrefix}-root`}>
      {/* Plan viewport */}
      <HudCard variant="glass" className="overflow-hidden p-0">
        <div
          ref={viewportRef}
          className="relative w-full select-none"
          style={viewportStyle}
          onClick={onViewportClick}
          data-testid={`${testidPrefix}-viewport`}
        >
          {planUrl ? (
            <img
              ref={planImgRef}
              src={planUrl}
              alt="Plan à calibrer"
              onLoad={handlePlanLoaded}
              className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-[12.5px]" style={{ color: 'var(--c-text-muted)' }}>
                Aucun plan disponible — sélectionne un lot avec un plan retenu.
              </p>
            </div>
          )}
          {planUrl && planDims.w > 0 && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`0 0 ${planDims.w} ${planDims.h}`}
              preserveAspectRatio="none"
            >
              {pointA && (
                <circle cx={pointA.x_rel * planDims.w} cy={pointA.y_rel * planDims.h}
                        r={6} fill="#5EEAD4" stroke="#0E0E10" strokeWidth="2" />
              )}
              {pointB && (
                <circle cx={pointB.x_rel * planDims.w} cy={pointB.y_rel * planDims.h}
                        r={6} fill="#5EEAD4" stroke="#0E0E10" strokeWidth="2" />
              )}
              {pointA && pointB && (
                <line
                  x1={pointA.x_rel * planDims.w} y1={pointA.y_rel * planDims.h}
                  x2={pointB.x_rel * planDims.w} y2={pointB.y_rel * planDims.h}
                  stroke="#5EEAD4" strokeWidth="1.8" strokeDasharray="6,4"
                />
              )}
            </svg>
          )}
        </div>
      </HudCard>

      {/* Controls */}
      <HudCard variant="glass">
        <div className="flex items-center justify-between mb-2">
          <span className="hud-eyebrow flex items-center gap-1">
            <Ruler className="w-3 h-3" />
            Calibration m/pixel
          </span>
          {scaleMPerPx !== null && (
            <span className="hud-tabular text-[11px] font-semibold" style={{ color: 'var(--c-gold)' }} data-testid={`${testidPrefix}-readout`}>
              1px = {scaleMPerPx.toFixed(4)}m
            </span>
          )}
        </div>

        {mode === 'idle' && scaleMPerPx === null && (
          <>
            <p className="text-[11px] mb-2" style={{ color: 'var(--c-text-muted)' }}>
              Pour des proportions fiables, calibre le plan en 2 clics + une distance connue
              (mur, porte, cote).
            </p>
            <button
              type="button"
              onClick={() => setMode('pick1')}
              className="hud-btn hud-btn-ghost hud-btn-sm w-full"
              data-testid={`${testidPrefix}-start`}
              disabled={!planUrl}
            >
              <Crosshair className="w-3 h-3" />
              Calibrer (2 clics + distance)
            </button>
          </>
        )}

        {(mode === 'pick1' || mode === 'pick2') && (
          <div className="text-[11.5px] flex items-start gap-1.5" style={{ color: '#5EEAD4' }}>
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              {mode === 'pick1'
                ? 'Clique le point A sur le plan.'
                : 'Clique le point B (distance connue).'}
            </span>
          </div>
        )}

        {mode === 'done' && scaleMPerPx === null && (
          <div className="space-y-2">
            <input
              type="number"
              min="0.1" step="0.1"
              value={meters}
              onChange={(e) => setMeters(e.target.value)}
              placeholder="Distance réelle en mètres (ex: 4.20)"
              className="w-full px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 text-[12px] text-white outline-none focus:border-[var(--c-gold)]"
              data-testid={`${testidPrefix}-meters`}
              autoFocus
            />
            <div className="flex gap-1.5">
              <button onClick={compute} className="hud-btn hud-btn-primary hud-btn-sm flex-1" data-testid={`${testidPrefix}-confirm`}>
                <Check className="w-3 h-3" />
                Calculer
              </button>
              <button onClick={reset} className="hud-btn hud-btn-ghost hud-btn-sm" data-testid={`${testidPrefix}-cancel`}>
                Annuler
              </button>
            </div>
          </div>
        )}

        {scaleMPerPx !== null && (
          <button onClick={reset} className="hud-btn hud-btn-ghost hud-btn-sm w-full mt-1" data-testid={`${testidPrefix}-reset`}>
            Recalibrer
          </button>
        )}
      </HudCard>
    </div>
  );
};

export default PlanCalibrator;
